import { Button, useToastContext } from '@payaca/ambrosia';
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import SwitchField from '@payaca/components/switchField/SwitchField';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { getIsRequiredFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import {
  createTimelogType,
  updateTimelogType,
} from '@payaca/store/timelogs/timelogsActions';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { PublicTimelogType } from '@payaca/types/timelogs';
import { useDispatch } from 'react-redux';
import './CreateEditTimelogTypeControl.sass';

type Props = {
  timelogType?: PublicTimelogType;
  onCreateEditSuccess?: () => void;
};

const CreateEditTimelogTypeControl: FunctionComponent<Props> = ({
  timelogType,
  onCreateEditSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const { pushError } = useToastContext();
  const initialFormState = useMemo(() => {
    return {
      type: timelogType?.type || '',
      description: timelogType?.description || '',
      isAvailableForGeneralTimelogs:
        timelogType?.isAvailableForGeneralTimelogs ?? false,
    };
  }, [timelogType]);
  const [isProcessing, setIsProcessing] = useState(false);

  const onFormSubmit = useCallback(
    (formState: { [key: string]: any }) => {
      setIsProcessing(true);
      if (timelogType?.publicId) {
        dispatch(
          updateTimelogType.request({
            timelogType: {
              publicId: timelogType.publicId,
              type: formState.type,
              description: formState.description,
              isAvailableForGeneralTimelogs:
                formState.isAvailableForGeneralTimelogs,
            },
            callback: () => {
              setIsProcessing(false);
              onCreateEditSuccess?.();
            },
            onErrorCallback: () => {
              setIsProcessing(false);
              pushError(`We couldn't save your changes`);
            },
          })
        );
      } else {
        dispatch(
          createTimelogType.request({
            timelogType: {
              type: formState.type,
              description: formState.description,
              isAvailableForGeneralTimelogs:
                formState.isAvailableForGeneralTimelogs,
            },
            callback: () => {
              setIsProcessing(false);
              onCreateEditSuccess?.();
            },
            onErrorCallback: () => {
              setIsProcessing(false);
              pushError(`We couldn't save your changes`);
            },
          })
        );
      }
    },
    [dispatch, onCreateEditSuccess, pushError, timelogType?.publicId]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <div className="type-field">
            <ValidatedFieldWrapper
              validationResult={validationState.type}
              isTouched={touchedState.type || false}
            >
              <BasicField
                name="type"
                value={formState.type || ''}
                isRequired={true}
                onChange={onFieldChange}
                onTouch={onFieldTouch}
                label="Type"
              />
            </ValidatedFieldWrapper>
          </div>
          <div className="description-field">
            <ValidatedFieldWrapper
              validationResult={validationState.description}
              isTouched={touchedState.description || false}
            >
              <BasicField
                name="description"
                value={formState.description || ''}
                onChange={onFieldChange}
                onTouch={onFieldTouch}
                label="Description"
              />
            </ValidatedFieldWrapper>
          </div>
          <div className="availability-field">
            <SwitchField
              name="isAvailableForGeneralTimelogs"
              label="Available for general time tracking"
              value={formState.isAvailableForGeneralTimelogs}
              onChange={onFieldChange}
              onTouch={onFieldTouch}
            />
          </div>
          <Button
            className="self-end mb-3"
            isProcessing={isProcessing}
            replaceContentWhenProcessing
            disabled={!isValid}
            onClick={() => !isProcessing && onFormSubmit(formState)}
            variant="link"
          >
            Save
          </Button>
        </>
      );
    },
    [isProcessing, onFormSubmit]
  );

  return (
    <div className="create-edit-timelog-type-control">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
        fieldValidators={{
          type: [getIsRequiredFieldValidator()],
        }}
      />
    </div>
  );
};

export default CreateEditTimelogTypeControl;
